import {
  AccountRequest,
  AccountUpdateRequest,
  DeleteAccountRequest,
  FinalizeRegistrationRequest,
  GetJWTResponse,
  Gigya,
  IsAvailableRequst,
  LinkAccountRequest,
  LoginError,
  LoginRequest,
  newsletterCheck,
  newsletterUpdate,
  RegisterRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  Response,
  SocialRequest,
  UserData,
  UserInfoRequest,
  UserResponse,
  VerificationRequest
} from '../../../gigya'
import { ActionContext, Module } from 'vuex'
import { USER_ACTION, USER_GETTER, USER_MUTATION, UserState } from './types'

import { ENV_GETTER } from '../env'

export interface PersistTokenRequest {
  token: string
  type: 'lToken' | 'rToken'
}

export function createUserModule<RootState> (namespaced = false): Module<UserState, RootState> {
  const decorateWithNewsletter = async (user: UserResponse, baseUrl?: string) => {
    const email = user.profile?.email
    if (email) {
      if (!user.data) {
        user.data = {}
      }
      try {
        const airshipData = await newsletterCheck(email, baseUrl)
        user.data.RTLHU_Newsletter = airshipData.channel?.installed && airshipData.channel?.commercial_opted_in && (!airshipData.channel?.commercial_opted_out || new Date(airshipData.channel?.commercial_opted_in) > new Date(airshipData.channel?.commercial_opted_out))
      } catch (error) {
        user.data.RTLHU_Newsletter = false
        console.warn(error)
      }
    }
  }
  return {
    namespaced,
    state () {
      return {
        user: null
      }
    },
    getters: {
      [USER_GETTER.API] (_state, _getters, _rootState, rootGetters) {
        const dataCenter = rootGetters[ENV_GETTER.ENV]?.('GIGYA_DATA_CENTER') || 'eu1'
        const apiKey = rootGetters[ENV_GETTER.ENV]?.('GIGYA_API_KEY') || ''

        return new Gigya(dataCenter, apiKey)
      },
      [USER_GETTER.USER] (state): UserData | null {
        return state.user
      }
    },
    mutations: {
      [USER_MUTATION.USER] (state: UserState, payload: UserData | null) {
        state.user = payload
      }
    },
    actions: {
      async [USER_ACTION.LOGIN] ({ getters, commit }: ActionContext<UserState, RootState>, payload: LoginRequest): Promise<UserResponse|LoginError> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.login(payload)
        const expireSeconds = payload.sessionExpiration || 0
        const expire = expireSeconds ? new Date(Date.now() + (expireSeconds * 1000)).getTime() : 0

        const userData = await userApi.setUser({
          UID: response.UID,
          profile: response.profile,
          data: response.data,
          sessionExpiration: expire
        })
        commit(USER_MUTATION.USER, userData)

        return response
      },
      async [USER_ACTION.LOGOUT] ({ getters, commit }: ActionContext<UserState, RootState>): Promise<Response> {
        const userApi = getters[USER_GETTER.API]

        const response = await userApi.accounts.logout({ UID: getters[USER_GETTER.USER]?.UID || '' })
        commit(USER_MUTATION.USER, await userApi.setUser(null))

        return response
      },
      async [USER_ACTION.REGISTER] ({ dispatch, getters, rootGetters }: ActionContext<UserState, RootState>, payload: RegisterRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]

        if (payload?.data?.RTLHU_Consent !== null && payload?.data?.RTLHU_Consent !== undefined) {
          payload.data.RTLHU_Consent_created_at = payload.data.RTLHU_Consent ? new Date() : null;
        }
        if (payload?.data?.RTLHU_Newsletter !== null && payload?.data?.RTLHU_Newsletter !== undefined) {
          payload.data.RTLHU_Newsletter_created_at = payload.data.RTLHU_Newsletter ? new Date() : null;
        }

        await userApi.accounts.initRegistration()
        const userResponse = await userApi.accounts.register({ finalizeRegistration: true, ...payload })

        if (userResponse.profile?.email && (userResponse.data ?? payload.data)?.RTLHU_Newsletter) {
          await newsletterUpdate({
            gigyaId: userResponse.UID,
            email: userResponse.profile.email,
            firstName: userResponse.profile.firstName,
            lastName: userResponse.profile.lastName,
            state: (userResponse.data ?? payload.data).RTLHU_Newsletter
          }, rootGetters[ENV_GETTER.ENV]?.('RTL_URL'))
        }

        return await dispatch(USER_ACTION.ACCOUNT_INFO, { UID: userResponse.UID })
      },
      async [USER_ACTION.RESET_PASSWORD] ({ getters, commit }: ActionContext<UserState, RootState>, payload: ResetPasswordRequest): Promise<ResetPasswordResponse> {
        const userApi = getters[USER_GETTER.API]

        commit(USER_MUTATION.USER, await userApi.setUser(null))

        return userApi.accounts.resetPassword(payload)
      },
      [USER_ACTION.SOCIAL] ({ getters }: ActionContext<UserState, RootState>, payload: SocialRequest): String {
        const userApi = getters[USER_GETTER.API]
        const response = userApi.accounts.social(payload)

        return response
      },
      async [USER_ACTION.ACCOUNT_INFO] ({ getters, commit, rootGetters }: ActionContext<UserState, RootState>, payload: AccountRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]

        if (!payload.extraProfileFields) {
          payload.extraProfileFields = 'hometown'
        }

        const userResponse = await userApi.accounts.getAccountInfo(payload)

        await decorateWithNewsletter(userResponse, rootGetters[ENV_GETTER.ENV]?.('RTL_URL'))

        const userData = await userApi.setUser({
          UID: userResponse.UID,
          profile: userResponse.profile,
          data: userResponse.data,
          sessionExpiration: 0
        })
        commit(USER_MUTATION.USER, userData)

        return userResponse
      },
      async [USER_ACTION.UPDATE_ACCOUNT] ({ state, getters, commit, rootGetters }: ActionContext<UserState, RootState>, payload: AccountUpdateRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]

        if (payload?.data?.RTLHU_Consent !== null && payload?.data?.RTLHU_Consent !== undefined) {
          payload.data.RTLHU_Consent_created_at = payload.data.RTLHU_Consent ? new Date() : null;
        }
        if (payload?.data?.RTLHU_Newsletter !== null && payload?.data?.RTLHU_Newsletter !== undefined) {
          payload.data.RTLHU_Newsletter_created_at = payload.data.RTLHU_Newsletter ? new Date() : null;

          if (payload.profile?.email) {
            await newsletterUpdate({
              gigyaId: payload.UID,
              email: payload.profile.email,
              firstName: payload.profile.firstName,
              lastName: payload.profile.lastName,
              state: payload.data.RTLHU_Newsletter
            }, rootGetters[ENV_GETTER.ENV]?.('RTL_URL'))
          }
        }

        const response = await userApi.accounts.updateAccount(payload)

        const userData = await userApi.setUser({
          ...state.user,
          profile: {
            ...(state?.user?.profile || {}),
            ...payload.profile
          },
          data: {
            ...(state?.user?.data || {}),
            ...payload.data
          }
        })
        commit(USER_MUTATION.USER, userData)

        return response
      },
      async [USER_ACTION.NOTIFY_LOGIN] ({ getters }: ActionContext<UserState, RootState>, payload: UserInfoRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.notifyLogin(payload)

        return response
      },
      async [USER_ACTION.PERSIST_TOKEN] ({ getters }: ActionContext<UserState, RootState>, payload: PersistTokenRequest): Promise<void> {
        const userApi = getters[USER_GETTER.API]
        await userApi.storage.set(payload.type, payload.token)
      },
      async [USER_ACTION.JWT] ({ state, getters }: ActionContext<UserState, RootState>): Promise<GetJWTResponse> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.getJWT({ UID: state.user?.UID })

        return response
      },
      async [USER_ACTION.DELETE_ACCOUNT]({ getters, commit }: ActionContext<UserState, RootState>, payload: DeleteAccountRequest): Promise<Response> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.deleteAccount(payload)
        const userData = await userApi.setUser(null)
        commit(USER_MUTATION.USER, userData)

        return response
      },
      async [USER_ACTION.IS_AVAILABLE]({ getters }: ActionContext<UserState, RootState>, payload: IsAvailableRequst): Promise<Response> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.isAvailableLogin(payload)

        return response
      },
      async [USER_ACTION.SEND_VERIFICATION]({ getters }: ActionContext<UserState, RootState>, payload: VerificationRequest): Promise<Response> {
        const userApi = getters[USER_GETTER.API]
        const response = await userApi.accounts.sendVerification(payload)

        return response
      },
      async [USER_ACTION.LINK_ACCOUNT]({ getters, commit, rootGetters }: ActionContext<UserState, RootState>, payload: LinkAccountRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]
        const userResponse = await userApi.accounts.linkAccount(payload)

        await decorateWithNewsletter(userResponse, rootGetters[ENV_GETTER.ENV]?.('RTL_URL'))

        const userData = await userApi.setUser({
          UID: userResponse.UID,
          profile: userResponse.profile,
          data: userResponse.data,
        })

        commit(USER_MUTATION.USER, userData)

        return userResponse
      },
      async [USER_ACTION.FINALIZE_REGISTRATION]({ getters, commit, rootGetters }: ActionContext<UserState, RootState>, payload: FinalizeRegistrationRequest): Promise<UserResponse> {
        const userApi = getters[USER_GETTER.API]
        payload.include = 'profile,data'
        const userResponse = await userApi.accounts.finalizeRegistration(payload)

        await decorateWithNewsletter(userResponse, rootGetters[ENV_GETTER.ENV]?.('RTL_URL'))

        const userData = await userApi.setUser({
          UID: userResponse.UID,
          profile: userResponse.profile,
          data: userResponse.data,
        })

        commit(USER_MUTATION.USER, userData)

        return userResponse
      },
      [USER_ACTION.FORCED_LOGOUT]({ getters, commit }: ActionContext<UserState, RootState>): void {
        const userApi = getters[USER_GETTER.API]
        userApi.setUser(null)
        return commit(USER_MUTATION.USER, null)
      },
    }
  }
}
